import React from 'react';
import LegalLayout, { LegalSection, LegalSubheading, LegalList } from './LegalLayout';

const PrivacyPolicy = () => {
  return (
    <LegalLayout title="Privacy Policy" effectiveDate="May 16, 2026" lastUpdated="May 16, 2026">
      <LegalSection>
        <p>
          This Privacy Policy explains how Got Next ("we," "us," or the "App"), operated by Lumenforge Studios LLC,
          collects, uses, and protects your information when you use our iOS application.
        </p>
      </LegalSection>

      <LegalSection heading="1. Information We Collect">
        <LegalSubheading>Account Information</LegalSubheading>
        <p>When you sign in with Apple, we receive:</p>
        <LegalList items={[
          'Your Apple ID-provided identifier (anonymized; we do not see your email if you choose to hide it)',
          'Your display name (if you provide one)',
        ]} />

        <LegalSubheading>League and Game Data</LegalSubheading>
        <p>You voluntarily provide:</p>
        <LegalList items={[
          'League names and codes',
          'Player names, positions, and jersey numbers',
          'Game scores, stats, dates, and locations',
        ]} />

        <LegalSubheading>Health Data (Pro only, optional)</LegalSubheading>
        <p>With your explicit permission, we read from Apple Health:</p>
        <LegalList items={[
          'Heart rate samples during games',
          'Active energy / calories burned during games',
          'Workout duration',
        ]} />
        <p>Health data is only collected when:</p>
        <LegalList items={[
          'You have an active Got Next Pro subscription, and',
          'You start a live game while wearing your Apple Watch',
        ]} />
        <p>
          We never share your health data with other users unless you explicitly enable the "Share health stats with
          league" toggle in Settings. Even then, only members of your league can see your health stats.
        </p>

        <LegalSubheading>Location Data</LegalSubheading>
        <p>With your explicit permission, we use your approximate location to:</p>
        <LegalList items={[
          "Calculate today's Ball Day Score (a weather-based outdoor basketball rating)",
        ]} />
        <p>We do not store your location coordinates. We use them once to fetch local weather and discard them.</p>

        <LegalSubheading>Purchase Data</LegalSubheading>
        <p>When you subscribe to Got Next Pro:</p>
        <LegalList items={[
          'Apple handles all payment processing; we do not see your payment details',
          'We receive a subscription status (active/inactive) from StoreKit',
        ]} />
      </LegalSection>

      <LegalSection heading="2. How We Use Your Information">
        <LegalList items={[
          "Display your league's games, stats, and rankings within the app",
          'Sync your data across your devices (iPhone, Apple Watch)',
          "Show today's Ball Day Score based on your location's weather",
          'Show your post-game health summary (Pro users)',
          'Allow league members to see your shared health stats (only if you opt in)',
          'Verify your Pro subscription entitlement',
        ]} />
      </LegalSection>

      <LegalSection heading="3. Third Parties">
        <p>We share data with the following service providers:</p>
        <LegalList items={[
          'Apple Inc. — for authentication (Sign in with Apple), subscription payments (StoreKit), weather data (WeatherKit), and Health data (HealthKit). Apple\'s privacy policy applies to data it handles.',
          'Supabase Inc. — our backend database provider, which stores your league, game, and stat data. Supabase processes data on our behalf and does not sell it.',
        ]} />
        <p>We do not sell, rent, or trade your personal information to third parties.</p>
      </LegalSection>

      <LegalSection heading="4. Data Retention">
        <LegalList items={[
          'League and game data is retained as long as your account is active',
          'If you delete a league, it enters a 30-day grace period before being permanently deleted',
          'If you delete your account, all your personal data is permanently removed within 30 days',
          'Health data is retained per game record indefinitely unless you delete the game',
        ]} />
      </LegalSection>

      <LegalSection heading="5. Your Rights">
        <p>You can:</p>
        <LegalList items={[
          'View all your data within the app',
          'Delete individual players, games, or leagues',
          'Disable health sharing at any time in More → Health',
          'Sign out and stop using the app at any time',
          'Request account deletion by emailing support@gotnext.app',
        ]} />
        <p>
          For California residents (CCPA), EU/UK residents (GDPR), and other applicable jurisdictions, you have
          additional rights including the right to access, correct, and delete your personal information. Contact us at
          the email above to exercise these rights.
        </p>
      </LegalSection>

      <LegalSection heading="6. Children's Privacy">
        <p>
          Got Next is not directed to children under 13. We do not knowingly collect personal information from children
          under 13. If you believe we have, please contact us so we can delete the data.
        </p>
      </LegalSection>

      <LegalSection heading="7. Security">
        <p>We use industry-standard practices to protect your data:</p>
        <LegalList items={[
          'HTTPS for all network communication',
          'Row-level security on the backend so users can only access their own data',
          "Apple's cryptographic verification for purchases",
          'No plain-text storage of authentication tokens',
        ]} />
      </LegalSection>

      <LegalSection heading="8. Changes to This Policy">
        <p>
          We may update this Privacy Policy from time to time. Material changes will be announced in the app and the
          "Last Updated" date will be revised. Continued use of the app after a change indicates acceptance.
        </p>
      </LegalSection>

      <LegalSection heading="9. Contact">
        <p>If you have questions about this Privacy Policy, contact us at:</p>
        <p>
          Email: <a href="mailto:support@gotnext.app" style={{ color: 'var(--color-primary)' }}>support@gotnext.app</a>
          <br />
          Lumenforge Studios LLC
        </p>
      </LegalSection>
    </LegalLayout>
  );
};

export default PrivacyPolicy;
